"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CanonicalProduct = exports.CanonicalBarcode = exports.CompanionAttributeValue = exports.DepositAttributeValue = exports.WELL_KNOWN_ATTRIBUTE_KEYS = exports.CanonicalAttribute = exports.ExciseStampNumber = exports.EXCISE_STAMP_NUMBER_PATTERN = exports.ExciseEntryMode = exports.ExciseStampPolicy = exports.EXCISE_STAMP_COUNT_MAX = exports.ItemStatus = exports.WeightCheckMode = exports.UnitOfMeasure = void 0;
exports.readDepositRecipe = readDepositRecipe;
exports.readCompanionRecipe = readCompanionRecipe;
const zod_1 = require("zod");
/**
 * Canonical product representation for cross-cutting consumers (CSV importer,
 * future REST adapters, sync protocol). The Catalog module's REST DTOs
 * derive from this shape; transports normalise into it.
 */
exports.UnitOfMeasure = zod_1.z.enum(['each', 'kg', 'g', 'l', 'ml']);
exports.WeightCheckMode = zod_1.z.enum(['presence', 'tolerance', 'disabled']);
exports.ItemStatus = zod_1.z.enum(['active', 'archived']);
/**
 * RL-439: the most excise stamps one unit of a product may carry
 * (`Item.exciseStampCount`, 1..this). Mirrored by a CHECK on `items`.
 */
exports.EXCISE_STAMP_COUNT_MAX = 99;
/**
 * FN-220: how an item's excise stamp is asked for at the till.
 *
 * - `required` — the till asks for the stamp and the line cannot be sold
 *   without it (what `isMarked: true` always meant).
 * - `none` — no stamp (`isMarked: false`).
 * - `optional` — the till asks for the stamp, and a cashier holding
 *   `till.excise-mark.skip` may sell the line without it.
 *
 * `isMarked` stays on every wire and always equals `policy !== 'none'`.
 */
exports.ExciseStampPolicy = zod_1.z.enum(['required', 'none', 'optional']);
/**
 * FN-159 / FN-223: how the till may take an item's excise stamp.
 *
 * - `scan_or_manual` — scanned, or typed by hand (the default, and what every
 *   till did before the field existed).
 * - `scan_only` — scanned only; the till offers no manual entry.
 *
 * Meaningful only while the item is marked (`exciseStampPolicy !== 'none'`).
 */
exports.ExciseEntryMode = zod_1.z.enum(['scan_or_manual', 'scan_only']);
/**
 * RL-439: the printed number of one excise stamp — four uppercase Latin or Cyrillic letters, then six
 * digits (`AAAA000000`). The till validates a keyed-in or scanned stamp
 * against this before it accepts it for a line of an `isMarked` item.
 *
 * Deliberately NOT applied to `SyncTransactionLine.exciseMarks`: that upload
 * contract also carries the longer code a scanned 2D mark yields, and
 * tightening it would dead-letter receipts from tills already in the field.
 */
exports.EXCISE_STAMP_NUMBER_PATTERN = /^[A-ZА-ЯЁІЇЄҐ]{4}[0-9]{6}(?![\s\S])/;
exports.ExciseStampNumber = zod_1.z
    .string()
    .regex(exports.EXCISE_STAMP_NUMBER_PATTERN, 'excise stamp must be four letters then six digits');
exports.CanonicalAttribute = zod_1.z.object({
    key: zod_1.z.string().min(1).max(64),
    value: zod_1.z.string().max(1024),
});
/**
 * Attribute keys the engine gives meaning to, as opposed to the arbitrary
 * key/value pairs a retailer may carry for their own reporting.
 *
 * Named here rather than left as loose strings because each one changes
 * behaviour somewhere — a typo in `agegroup` is a promotion that silently
 * stops excluding minors, which is the kind of failure nobody notices until
 * it matters. Matching is case-insensitive at the consumer; these are the
 * canonical spellings.
 */
exports.WELL_KNOWN_ATTRIBUTE_KEYS = {
    /** Barcode-level. Distinguishes variants of one item (YUKI-3). */
    size: 'size',
    /** Product-level. Promotion include/exclude grouping (YUKI-4). */
    segment: 'segment',
    /** Product-level. Promotion include/exclude by age band (YUKI-5). */
    ageGroup: 'agegroup',
    /**
     * Product-level. The container deposit sold with each unit of this item
     * (MICRO-43): `<depositSku>|<unitsPerSoldUnit>`, see `DepositAttributeValue`.
     */
    deposit: 'deposit',
    /**
     * Product-level, sibling of `deposit` (FN-227). An ordinary sold companion
     * item (e.g. a bag) rung per unit of this item, NOT a fiscal deposit:
     * `<companionSku>|<unitsPerItemUnit>`, see `CompanionAttributeValue`.
     */
    companion: 'companion',
};
/**
 * MICRO-43 — the value of the `deposit` attribute: `<depositSku>|<unitsPerSoldUnit>`.
 *
 * - `depositSku`: the deposit item's SKU (e.g. `VXTRPET`). Its own store price
 *   is the deposit amount; a store without a price for it cannot sell the
 *   parent (owner Q7).
 * - `unitsPerSoldUnit`: deposits per unit sold, 1–999 (a 6-pack is 6, owner
 *   Q8). Always positive here, whatever sign the ERP stores (NAV's linked-item
 *   quantity is negative).
 *
 * Nothing is guessed: any other shape is refused, and the item it is on is
 * not sellable (fail closed, as the design's recipe rule).
 */
exports.DepositAttributeValue = zod_1.z
    .string()
    .regex(/^([^|\s]{1,64})\|([1-9][0-9]{0,2})$/, 'deposit must be <depositSku>|<unitsPerSoldUnit>')
    .transform((value) => {
    const [depositSku = '', units = ''] = value.split('|');
    return { depositSku, unitsPerSoldUnit: Number(units) };
});
/**
 * The deposit recipe of an item from its product attributes. The key is
 * matched case-insensitively, as every well-known key; a malformed value or
 * more than one `deposit` attribute is `invalid`, never "no deposit".
 */
function readDepositRecipe(attributes) {
    const found = attributes.filter((attribute) => attribute.key.toLowerCase() === exports.WELL_KNOWN_ATTRIBUTE_KEYS.deposit);
    if (found.length === 0)
        return { kind: 'none' };
    if (found.length > 1)
        return { kind: 'invalid' };
    const parsed = exports.DepositAttributeValue.safeParse(found[0]?.value);
    return parsed.success ? { kind: 'deposit', ...parsed.data } : { kind: 'invalid' };
}
/**
 * FN-227 — the value of the `companion` attribute: `<companionSku>|<unitsPerItemUnit>`,
 * e.g. `BAG01|1`: a pizza sold with one bag per pizza.
 *
 * - `companionSku`: the companion item's SKU. It is an ordinary item, sold at
 *   its own store price and counted as a sale, unlike a deposit.
 * - `unitsPerItemUnit`: companions per unit of the item, 1–999.
 *
 * Same grammar as `DepositAttributeValue`; any other shape is refused.
 */
exports.CompanionAttributeValue = zod_1.z
    .string()
    .regex(/^([^|\s]{1,64})\|([1-9][0-9]{0,2})$/, 'companion must be <companionSku>|<unitsPerItemUnit>')
    .transform((value) => {
    const [companionSku = '', units = ''] = value.split('|');
    return { companionSku, unitsPerItemUnit: Number(units) };
});
/**
 * The companion recipe of an item from its product attributes, read like
 * `readDepositRecipe`: key matched case-insensitively; a malformed value or
 * more than one `companion` attribute is `invalid`, never "no companion".
 * Independent of the deposit: an item may carry both.
 */
function readCompanionRecipe(attributes) {
    const found = attributes.filter((attribute) => attribute.key.toLowerCase() === exports.WELL_KNOWN_ATTRIBUTE_KEYS.companion);
    if (found.length === 0)
        return { kind: 'none' };
    if (found.length > 1)
        return { kind: 'invalid' };
    const parsed = exports.CompanionAttributeValue.safeParse(found[0]?.value);
    return parsed.success ? { kind: 'companion', ...parsed.data } : { kind: 'invalid' };
}
exports.CanonicalBarcode = zod_1.z.object({
    barcode: zod_1.z.string().min(1).max(64),
    isPrimary: zod_1.z.boolean().default(false),
    /**
     * Attributes that distinguish THIS barcode from its siblings under the same
     * item (YUKI-3).
     *
     * Deliberately separate from the product-level `attributes` below, because
     * they answer different questions. A product attribute describes the item —
     * its segment, its age group — and is the same whichever barcode was
     * scanned. A barcode attribute is what makes one barcode not another: for an
     * apparel retailer, size.
     *
     * That distinction is load-bearing for the cashier flow. Typing an item code
     * has to produce a list the cashier can choose from, and a list of barcodes
     * is unusable — `SKT000575219` tells nobody anything. A list of *sizes* is
     * the same list made answerable. Flattening these onto the product would
     * leave every variant claiming every size.
     */
    attributes: zod_1.z.array(exports.CanonicalAttribute).default([]),
});
exports.CanonicalProduct = zod_1.z.object({
    sku: zod_1.z.string().min(1).max(64),
    name: zod_1.z.string().min(1).max(255),
    /**
     * The name printed on a fiscal receipt, when it must differ from [name]
     * (YUKI-35).
     *
     * Absent means "use `name`", which is what every deployment before this
     * meant and still means. Only Ukrainian LLC-mode trading needs the split:
     * the fiscal document carries a legally-constrained product description
     * while the scanning screen keeps the name the cashier recognises. Getting
     * these the wrong way round prints an internal shorthand on a customer's
     * fiscal receipt.
     */
    posName: zod_1.z.string().min(1).max(255).optional(),
    description: zod_1.z.string().max(4096).optional(),
    /** Either a categoryId (existing) or a categoryName (importer may create). */
    categoryId: zod_1.z.string().optional(),
    categoryName: zod_1.z.string().optional(),
    unitOfMeasure: exports.UnitOfMeasure.default('each'),
    taxCategory: zod_1.z.string().min(1).max(32),
    ageRestriction: zod_1.z.number().int().min(0).max(120).default(0),
    isVariableWeight: zod_1.z.boolean().default(false),
    skipBaggingAllowed: zod_1.z.boolean().default(false),
    weightCheckMode: exports.WeightCheckMode.default('presence'),
    weightTolerancePctOverride: zod_1.z.number().min(0).max(100).nullable().optional(),
    status: exports.ItemStatus.default('active'),
    barcodes: zod_1.z.array(exports.CanonicalBarcode).default([]),
    attributes: zod_1.z.array(exports.CanonicalAttribute).default([]),
});
//# sourceMappingURL=catalog.js.map